"use client";
import {JSX, ReactNode} from "react";

import Link from "next/link";

import {Spinner} from "@/shared/ui/feedback/spinner";

import {PolymorphicButton} from "./types";

import "@/shared/ui/buttons/styles/button-base.scss";

// ButtonBase specific types
type ButtonVariant = "primary" | "primary-outline" | "secondary" | "secondary-outline";
export type ButtonSize = "xs-s" | "xs" | "sm" | "md" | "lg" | "lg-fixed";
type ButtonWidth = "full" | "auto" | "fit";
type ButtonWeight = "normal" | "medium" | "semibold" | "bold";

type ButtonBaseSpecificProps = {
    width?: ButtonWidth;
    size?: ButtonSize;
    variant?: ButtonVariant;
    weight?: ButtonWeight;
    children: ReactNode;
    prefetch?: boolean;
    loading?: boolean;
};

type ButtonBaseProps = PolymorphicButton<ButtonBaseSpecificProps>;

// Button element component
function ButtonElement(props: ButtonBaseProps & {as?: "button"}): JSX.Element {
    const {variant, size, width, weight, children, className, type = "button", loading, disabled, onClick, ...rest} = props;

    const baseClasses = `button-base ${variant} ${size} ${width}${weight ? ` button-base--${weight}` : ""}${loading ? " button-base--loading" : ""}`;
    const finalClassName = className ? `${baseClasses} ${className}` : baseClasses;

    const handleClick = loading ? undefined : onClick;

    return (
        <button type={type} className={finalClassName} disabled={disabled} onClick={handleClick} {...rest}>
            {loading ? <Spinner size="small" /> : children}
        </button>
    );
}

// Link element component
function ButtonLinkElement(props: ButtonBaseProps & {as: "link"}): JSX.Element {
    const {as: _as, variant, size, width, weight, children, className, target, prefetch = false, ...linkProps} = props;

    const baseClasses = `button-base ${variant} ${size} ${width}${weight ? ` button-base--${weight}` : ""}`;
    const finalClassName = className ? `${baseClasses} ${className}` : baseClasses;

    return (
        <Link className={finalClassName} prefetch={prefetch} {...(target && {target})} {...linkProps}>
            {children}
        </Link>
    );
}

// Main ButtonBase component
export function ButtonBase({variant = "primary", size = "md", width = "auto", weight, ...rest}: ButtonBaseProps): JSX.Element {
    if (rest.as === "link") {
        return (
            <ButtonLinkElement {...rest} variant={variant} size={size} width={width} weight={weight}>
                {rest.children}
            </ButtonLinkElement>
        );
    }

    return (
        <ButtonElement {...rest} variant={variant} size={size} width={width} weight={weight}>
            {rest.children}
        </ButtonElement>
    );
}
